import React, { useState, useEffect } from 'react';
import campaignsAPI from '../api/campaigns.js';
import '../styles/CampaignDrawer.css';

const STEPS = ['Template', 'Subject', 'Audience', 'Review & Send'];

export default function CampaignDrawer({ template, onClose, onSent }) {
  const [step, setStep] = useState(0);
  const [subject, setSubject] = useState(template?.name || '');
  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentCount, setSegmentCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    campaignsAPI.getSegments().then(r => setSegments(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSegment) {
      setSegmentCount(null);
      campaignsAPI.getSegmentCount(selectedSegment.key)
        .then(r => setSegmentCount(r.data?.count ?? 0))
        .catch(() => setSegmentCount(0));
    }
  }, [selectedSegment]);

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const created = await campaignsAPI.create({
        name: `${template?.name} — ${new Date().toLocaleDateString()}`,
        templateId: template?._id,
        subject,
        contactSource: { type: 'knkt-segment', ids: [selectedSegment.key], label: selectedSegment.label },
      });
      if (!created.success) throw new Error(created.error || 'Failed to create campaign');
      await campaignsAPI.send(created.data._id);
      onSent && onSent(created.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Send failed');
      setSending(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!template;
    if (step === 1) return subject.trim().length > 0;
    if (step === 2) return !!selectedSegment;
    return true;
  };

  return (
    <div className="campaign-drawer-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="campaign-drawer">
        <div className="campaign-drawer-header">
          <h2>Send Campaign</h2>
          <button className="drawer-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="campaign-stepper">
          {STEPS.map((label, i) => (
            <div key={i} className={`stepper-step ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
              onClick={() => i < step && setStep(i)}>
              {label}
            </div>
          ))}
        </div>

        <div className="campaign-drawer-body">
          {step === 0 && (
            <div>
              <div className="drawer-step-label">Selected Template</div>
              <div className="template-preview-card">
                <div>
                  <div className="tmpl-name">{template?.name || 'No template'}</div>
                  <div className="tmpl-sub">{template?.category || 'Platform'}</div>
                </div>
              </div>
              <p style={{ color: '#666', fontSize: 13, marginTop: 12 }}>
                This template will be sent as-is. To edit it first, close this drawer and use the editor.
              </p>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="drawer-step-label">Email Subject Line</div>
              <input className="drawer-input" placeholder="Enter subject line..." value={subject}
                onChange={e => setSubject(e.target.value)} autoFocus />
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="drawer-step-label">Select Audience Segment</div>
              {segments.map(seg => (
                <div key={seg.key}
                  className={`contact-source-option ${selectedSegment?.key === seg.key ? 'selected' : ''}`}
                  onClick={() => setSelectedSegment(seg)}>
                  <div className="source-label">{seg.label}</div>
                  <div className="source-count">
                    {selectedSegment?.key === seg.key && segmentCount !== null
                      ? `${segmentCount.toLocaleString()} users`
                      : 'Click to preview count'}
                  </div>
                </div>
              ))}
              {selectedSegment && segmentCount !== null && (
                <div className="recipient-preview">
                  Sending to <strong>{segmentCount.toLocaleString()}</strong> users
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="drawer-step-label">Review Before Sending</div>
              <div className="review-row">
                <span className="review-label">Template</span>
                <span className="review-value">{template?.name}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Subject</span>
                <span className="review-value">{subject}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Audience</span>
                <span className="review-value">{selectedSegment?.label}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Recipients</span>
                <span className="review-value">{(segmentCount || 0).toLocaleString()}</span>
              </div>
              {error && <div className="drawer-error">{error}</div>}
            </div>
          )}
        </div>

        <div className="campaign-drawer-footer">
          {step > 0 && <button className="drawer-btn-back" onClick={() => setStep(s => s - 1)}>Back</button>}
          {step < STEPS.length - 1
            ? <button className="drawer-btn-next" onClick={() => setStep(s => s + 1)} disabled={!canProceed()}>Next →</button>
            : <button className="drawer-btn-send" onClick={handleSend} disabled={sending || !canProceed()}>
                {sending ? 'Sending...' : `Send to ${(segmentCount || 0).toLocaleString()} →`}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
